import React, { useEffect, useState } from "react";
import axios from "axios";
import { downloadPdf } from "../lib/downloadPdf";
import InvoiceManageModal from "./modalComponents/payment/InvoiceManageModal";
import ClassSelectionModal from "./modalComponents/payment/ClassSelectionModal";
import CreateVideoInvoiceModal from "./modalComponents/payment/CreateVideoInvoiceModal";
import CustomLiveInvoiceModal from "./modalComponents/payment/CustomLiveInvoiceModal";
import DiscountModal from "./modalComponents/payment/DiscountModal";
import ReceiptManageModal from "./modalComponents/payment/ReceiptManageModal";
import CollectPaymentModal from "./modalComponents/payment/CollectPaymentModal";
import PaymentTypeModal from "./modalComponents/payment/PaymentTypeModal";
import PaymentOverview from "./modalComponents/payment/PaymentOverview";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "live-invoices", label: "Live Invoices" },
  { id: "video-invoices", label: "Video Invoices" },
  { id: "live-receipts", label: "Live Receipts" },
  { id: "video-receipts", label: "Video Receipts" },
];

function MonthTable({ months, emptyText, onManage }) {
  return (
    <table className="w-full border-collapse bg-white shadow-sm rounded-lg overflow-hidden">
      <thead>
        <tr className="bg-gray-100">
          <th className="border px-6 py-2 text-left font-semibold text-gray-700">Year-Month</th>
          <th className="border px-6 py-2 text-left font-semibold text-gray-700">Action</th>
        </tr>
      </thead>
      <tbody>
        {months.map((m, idx) => (
          <tr key={idx} className="hover:bg-gray-50">
            <td className="border px-6 py-2">{m.yearMonth}</td>
            <td className="border px-6 py-2">
              <button
                onClick={() => onManage(m.yearMonth)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded font-medium transition-colors"
              >
                Manage
              </button>
            </td>
          </tr>
        ))}
        {months.length === 0 && (
          <tr>
            <td colSpan={2} className="text-center py-10 text-gray-500">
              {emptyText}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

export default function Payment() {
  const [activeTab, setActiveTab] = useState("overview");
  const [liveMonths, setLiveMonths] = useState([]);
  const [videoMonths, setVideoMonths] = useState([]);

  const [liveInvoiceMonth, setLiveInvoiceMonth] = useState(null);
  const [showLiveInvoices, setShowLiveInvoices] = useState(false);
  const [liveInvoices, setLiveInvoices] = useState([]);

  const [videoInvoiceMonth, setVideoInvoiceMonth] = useState(null);
  const [showVideoInvoices, setShowVideoInvoices] = useState(false);
  const [videoInvoices, setVideoInvoices] = useState([]);

  const [liveReceiptMonth, setLiveReceiptMonth] = useState(null);
  const [showLiveReceipts, setShowLiveReceipts] = useState(false);
  const [liveReceipts, setLiveReceipts] = useState([]);

  const [videoReceiptMonth, setVideoReceiptMonth] = useState(null);
  const [showVideoReceipts, setShowVideoReceipts] = useState(false);
  const [videoReceipts, setVideoReceipts] = useState([]);

  const [showClassModal, setShowClassModal] = useState(false);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [showCustomInvoice, setShowCustomInvoice] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);

  const [showLiveCollect, setShowLiveCollect] = useState(false);
  const [livePendingPayments, setLivePendingPayments] = useState([]);
  const [showVideoCollect, setShowVideoCollect] = useState(false);
  const [videoPendingPayments, setVideoPendingPayments] = useState([]);

  const [showPaymentTypeModal, setShowPaymentTypeModal] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [isVideoPayment, setIsVideoPayment] = useState(false);
  const [paymentType, setPaymentType] = useState("cash");
  const [expiryDate, setExpiryDate] = useState("");

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/admin/months`)
      .then((res) => setLiveMonths(res.data || []))
      .catch((err) => console.error("Error fetching live months:", err));

    axios
      .get(`${process.env.REACT_APP_API_URL}/admin/video/months`)
      .then((res) => setVideoMonths(res.data || []))
      .catch((err) => console.error("Error fetching video months:", err));
  }, []);

  const refreshLiveInvoices = async (yearMonth) => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin?month=${yearMonth}`);
    setLiveInvoices(res.data || []);
  };

  const refreshVideoInvoices = async (yearMonth) => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/admin/video?month=${yearMonth}`
    );
    setVideoInvoices(res.data || []);
  };

  const refreshLiveReceipts = async (yearMonth) => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin?month=${yearMonth}`);
    setLiveReceipts(res.data || []);
  };

  const refreshVideoReceipts = async (yearMonth) => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/admin/video?month=${yearMonth}`
    );
    setVideoReceipts(res.data || []);
  };

  const handleManageInvoices = async (yearMonth, tab) => {
    if (tab === "live") {
      setLiveInvoiceMonth(yearMonth);
      setShowLiveInvoices(true);
      try {
        await refreshLiveInvoices(yearMonth);
      } catch (err) {
        console.error("Error fetching live invoices:", err);
        setLiveInvoices([]);
      }
    } else {
      setVideoInvoiceMonth(yearMonth);
      setShowVideoInvoices(true);
      try {
        await refreshVideoInvoices(yearMonth);
      } catch (err) {
        console.error("Error fetching video invoices:", err);
        setVideoInvoices([]);
      }
    }
  };

  const handleManageReceipts = async (yearMonth, isLive) => {
    if (isLive) {
      setLiveReceiptMonth(yearMonth);
      setShowLiveReceipts(true);
      try {
        await refreshLiveReceipts(yearMonth);
      } catch (err) {
        console.error("Error fetching live receipts:", err);
        setLiveReceipts([]);
      }
    } else {
      setVideoReceiptMonth(yearMonth);
      setShowVideoReceipts(true);
      try {
        await refreshVideoReceipts(yearMonth);
      } catch (err) {
        console.error("Error fetching video receipts:", err);
        setVideoReceipts([]);
      }
    }
  };

  const handlePrintAll = async (tab) => {
    const selectedMonth = tab === "live" ? liveInvoiceMonth : videoInvoiceMonth;
    if (!selectedMonth) return;

    const url =
      tab === "live"
        ? `${process.env.REACT_APP_API_URL}/admin/pdf/all?month=${selectedMonth}`
        : `${process.env.REACT_APP_API_URL}/admin/video/pdf/all?month=${selectedMonth}`;

    try {
      await downloadPdf(
        url,
        tab === "live" ? "live_invoices.pdf" : "video_invoices.pdf"
      );
    } catch (err) {
      console.error("Error downloading PDF:", err);
      alert("Error generating PDF.");
    }
  };

  const handlePrintInvoice = async (invoice, tab) => {
    try {
      const url =
        tab === "live"
          ? `${process.env.REACT_APP_API_URL}/admin/pdf?student_id=${invoice.student_id}&invoice_ref=${invoice.invoice_ref}`
          : `${process.env.REACT_APP_API_URL}/admin/video/pdf?video_invoice_ref=${invoice.invoice_ref}`;
      const fallback =
        tab === "live"
          ? `${invoice.invoice_ref || "invoice"}.pdf`
          : `${invoice.invoice_ref || "video_invoice"}.pdf`;
      await downloadPdf(url, fallback);
    } catch (err) {
      console.error("Error downloading PDF:", err);
      alert("Error generating PDF.");
    }
  };

  const handleDeleteInvoice = async (invoice, tab) => {
    const refId = invoice.invoice_ref;
    const confirmed = window.confirm(
      `Are you sure you want to delete invoice ${refId}?\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const res = await axios.delete(`${process.env.REACT_APP_API_URL}/admin/${refId}`);
      if (res.data.error) {
        throw new Error(res.data.error || "Failed to delete invoice");
      }
      alert("Invoice deleted successfully.");
      if (tab === "live" && liveInvoiceMonth) {
        await refreshLiveInvoices(liveInvoiceMonth);
      } else if (tab === "video" && videoInvoiceMonth) {
        await refreshVideoInvoices(videoInvoiceMonth);
      }
    } catch (err) {
      console.error("Error deleting invoice:", err);
      alert(err.response?.data?.error || err.message || "Failed to delete invoice.");
    }
  };

  const handlePrintReceipt = async (receipt, isLive) => {
    try {
      if (isLive) {
        await downloadPdf(
          `${process.env.REACT_APP_API_URL}/admin/receipts/pdf?invoice_ref=${receipt.invoice_ref}`,
          `receipt_${receipt.invoice_ref}.pdf`
        );
      } else {
        const videoRef = receipt.video_invoice_ref || receipt.invoice_ref;
        await downloadPdf(
          `${process.env.REACT_APP_API_URL}/admin/video/receipts/pdf?video_invoice_ref=${videoRef}`,
          `video_receipt_${videoRef}.pdf`
        );
      }
    } catch (err) {
      console.error("Error downloading receipt PDF:", err);
      alert("Error generating PDF receipt.");
    }
  };

  const handleCollectPayments = async (isLive) => {
    if (isLive) {
      setShowLiveCollect(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/payments?status=pending`
        );
        setLivePendingPayments(res.data || []);
      } catch (err) {
        console.error("Error fetching live pending payments:", err);
        setLivePendingPayments([]);
      }
    } else {
      setShowVideoCollect(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/video/payments?status=pending`
        );
        setVideoPendingPayments(res.data || []);
      } catch (err) {
        console.error("Error fetching video pending payments:", err);
        setVideoPendingPayments([]);
      }
    }
  };

  const openPaymentTypeModal = (paymentId, isVideo) => {
    if (!paymentId) {
      alert("Error: No payment ID selected.");
      return;
    }
    setSelectedPaymentId(paymentId);
    setIsVideoPayment(isVideo);
    setPaymentType("cash");
    if (isVideo) {
      const today = new Date();
      const twoMonthsLater = new Date(
        today.getFullYear(),
        today.getMonth() + 2,
        today.getDate()
      );
      setExpiryDate(twoMonthsLater.toISOString().split("T")[0]);
    } else {
      setExpiryDate("");
    }
    setShowPaymentTypeModal(true);
  };

  const handleConfirmPayment = async () => {
    if (!paymentType) {
      alert("Please select a payment type.");
      return;
    }
    if (!selectedPaymentId) {
      alert("Error: No payment ID selected.");
      return;
    }

    if (isVideoPayment) {
      if (!expiryDate) {
        alert("Please provide an expiry date for video payment.");
        return;
      }
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/admin/video/payments/${selectedPaymentId}/collect`,
          { payment_type: paymentType, expiry_date: expiryDate }
        );
        if (res.data.success) {
          setVideoPendingPayments((prev) =>
            prev.filter((p) => p.video_payment_id !== selectedPaymentId)
          );
          setShowPaymentTypeModal(false);
          setExpiryDate("");
          alert("Video payment marked as 'paid' successfully!");
        } else {
          alert(`Failed to mark video payment as paid: ${res.data.error || "Unknown error"}`);
        }
      } catch (err) {
        console.error("Error updating video payment status:", err);
        alert(err.response?.data?.error || "Error updating video payment status.");
      }
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/payments/${selectedPaymentId}/collect`,
        { payment_type: paymentType }
      );
      if (res.data.success) {
        setLivePendingPayments((prev) =>
          prev.filter((p) => p.payment_id !== selectedPaymentId)
        );
        setShowPaymentTypeModal(false);
        alert("Payment marked as 'paid' successfully!");
      } else {
        alert(`Failed to mark payment as paid: ${res.data.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Error updating payment status:", err);
      alert(err.response?.data?.error || "Error updating payment status.");
    }
  };

  const handleDiscountApplied = async (yearMonth) => {
    try {
      if (liveInvoiceMonth === yearMonth) await refreshLiveInvoices(yearMonth);
      if (videoInvoiceMonth === yearMonth) await refreshVideoInvoices(yearMonth);
      if (liveReceiptMonth === yearMonth) await refreshLiveReceipts(yearMonth);
      if (videoReceiptMonth === yearMonth) await refreshVideoReceipts(yearMonth);
    } catch (err) {
      console.error("Error refreshing after discount:", err);
    }
  };

  const handleCustomInvoiceGenerated = async (endDate) => {
    const invoiceMonth = endDate.slice(0, 7);
    if (liveInvoiceMonth && liveInvoiceMonth === invoiceMonth) {
      try {
        await refreshLiveInvoices(liveInvoiceMonth);
      } catch (err) {
        console.error("Error refreshing live invoices:", err);
      }
    }
  };

  const handleVideoInvoiceCreated = async () => {
    if (!videoInvoiceMonth) return;
    try {
      await refreshVideoInvoices(videoInvoiceMonth);
    } catch (err) {
      console.error("Error refreshing video invoices:", err);
    }
  };

  return (
    <div className="p-8 min-h-screen overflow-y-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payment</h1>
        <div className="mt-2 border-t border-gray-300 w-64"></div>
      </div>

      <div className="flex flex-col gap-4 mb-8">
        <div className="flex flex-wrap items-center gap-3">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-md font-medium transition-colors ${
                activeTab === tab.id
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {(activeTab === "live-invoices" || activeTab === "video-invoices") && (
            <button
              onClick={() => setShowDiscount(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-md font-medium shadow-sm transition-all hover:shadow min-w-[170px]"
            >
              Discount Management
            </button>
          )}
          {activeTab === "live-invoices" && (
            <button
              onClick={() => setShowCustomInvoice(true)}
              className="bg-green-800 hover:bg-green-900 text-white px-6 py-2.5 rounded-md font-medium shadow-sm transition-all hover:shadow min-w-[170px]"
            >
              Customize Live Invoice
            </button>
          )}
          {activeTab === "video-invoices" && (
            <button
              onClick={() => setShowCreateInvoice(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-md font-medium shadow-sm transition-all hover:shadow min-w-[170px]"
            >
              Create Video Invoice
            </button>
          )}
          {activeTab === "live-receipts" && (
            <button
              onClick={() => handleCollectPayments(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-md font-medium shadow-sm transition-all hover:shadow min-w-[190px]"
            >
              Collect Live Payments
            </button>
          )}
          {activeTab === "video-receipts" && (
            <button
              onClick={() => handleCollectPayments(false)}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-md font-medium shadow-sm transition-all hover:shadow min-w-[190px]"
            >
              Collect Video Payments
            </button>
          )}
        </div>
      </div>

      {activeTab === "overview" && <PaymentOverview />}

      {activeTab === "live-invoices" && (
        <MonthTable
          months={liveMonths}
          emptyText="No months available"
          onManage={(yearMonth) => handleManageInvoices(yearMonth, "live")}
        />
      )}
      {activeTab === "video-invoices" && (
        <MonthTable
          months={videoMonths}
          emptyText="No months available"
          onManage={(yearMonth) => handleManageInvoices(yearMonth, "video")}
        />
      )}
      {activeTab === "live-receipts" && (
        <MonthTable
          months={liveMonths}
          emptyText="No live receipt months found"
          onManage={(yearMonth) => handleManageReceipts(yearMonth, true)}
        />
      )}
      {activeTab === "video-receipts" && (
        <MonthTable
          months={videoMonths}
          emptyText="No video receipt months found"
          onManage={(yearMonth) => handleManageReceipts(yearMonth, false)}
        />
      )}

      <InvoiceManageModal
        isOpen={showLiveInvoices}
        tab="live"
        selectedMonth={liveInvoiceMonth}
        invoices={liveInvoices}
        onClose={() => setShowLiveInvoices(false)}
        onPrintAll={() => handlePrintAll("live")}
        onPrintByClass={() => setShowClassModal(true)}
        onPrintOne={(invoice) => handlePrintInvoice(invoice, "live")}
        onDelete={(invoice) => handleDeleteInvoice(invoice, "live")}
      />
      <InvoiceManageModal
        isOpen={showVideoInvoices}
        tab="video"
        selectedMonth={videoInvoiceMonth}
        invoices={videoInvoices}
        onClose={() => setShowVideoInvoices(false)}
        onPrintAll={() => handlePrintAll("video")}
        onPrintOne={(invoice) => handlePrintInvoice(invoice, "video")}
        onDelete={(invoice) => handleDeleteInvoice(invoice, "video")}
      />
      <ClassSelectionModal
        isOpen={showClassModal}
        month={liveInvoiceMonth}
        onClose={() => setShowClassModal(false)}
      />
      <CreateVideoInvoiceModal
        isOpen={showCreateInvoice}
        onClose={() => setShowCreateInvoice(false)}
        onCreated={handleVideoInvoiceCreated}
      />
      <CustomLiveInvoiceModal
        isOpen={showCustomInvoice}
        onClose={() => setShowCustomInvoice(false)}
        onGenerated={handleCustomInvoiceGenerated}
      />
      <DiscountModal
        isOpen={showDiscount}
        onClose={() => setShowDiscount(false)}
        onApplied={handleDiscountApplied}
      />
      <ReceiptManageModal
        isOpen={showLiveReceipts}
        isLive
        selectedMonth={liveReceiptMonth}
        receipts={liveReceipts}
        onClose={() => setShowLiveReceipts(false)}
        onPrint={(receipt) => handlePrintReceipt(receipt, true)}
      />
      <ReceiptManageModal
        isOpen={showVideoReceipts}
        isLive={false}
        selectedMonth={videoReceiptMonth}
        receipts={videoReceipts}
        onClose={() => setShowVideoReceipts(false)}
        onPrint={(receipt) => handlePrintReceipt(receipt, false)}
      />
      <CollectPaymentModal
        isOpen={showLiveCollect}
        isLive
        payments={livePendingPayments}
        onClose={() => setShowLiveCollect(false)}
        onConfirm={(paymentId) => openPaymentTypeModal(paymentId, false)}
      />
      <CollectPaymentModal
        isOpen={showVideoCollect}
        isLive={false}
        payments={videoPendingPayments}
        onClose={() => setShowVideoCollect(false)}
        onConfirm={(paymentId) => openPaymentTypeModal(paymentId, true)}
      />
      <PaymentTypeModal
        isOpen={showPaymentTypeModal}
        isVideo={isVideoPayment}
        paymentType={paymentType}
        expiryDate={expiryDate}
        onPaymentTypeChange={setPaymentType}
        onExpiryDateChange={setExpiryDate}
        onClose={() => {
          setShowPaymentTypeModal(false);
          setExpiryDate("");
        }}
        onConfirm={handleConfirmPayment}
      />
    </div>
  );
}
